/**
 * Branding Domain Models
 *
 * Domain types for club branding: a small set of semantic colours, three
 * typography slots, and four logo assets.
 *
 * The slot vocabularies live here as `const` tuples rather than in the utils
 * layer, so the adapter and the validators both derive from one source and
 * adding a ninth colour cannot leave either of them silently ignoring it.
 *
 * Requirements: 2.2, 2.5, 5.6, 8.2
 */

/**
 * The eight brandable colour slots.
 *
 * Four background/foreground pairs. A slot left `null` means the Toptracer
 * default applies to that slot — fallback is per field, not per brand.
 */
export const BRAND_COLOR_SLOTS = [
  'accent',
  'onAccent',
  'header',
  'onHeader',
  'surface',
  'onSurface',
  'raised',
  'onRaised',
] as const;

export type BrandColorSlot = (typeof BRAND_COLOR_SLOTS)[number];

/**
 * Every colour slot, present as a value or an explicit `null`.
 *
 * Values are typed `string` rather than a branded hex type because a stored
 * record is an untrusted boundary: the backend validates `#RRGGBB`, but a
 * record written before a validation change could still hold something else,
 * and the editor needs to show an operator the bad value so they can fix it.
 */
export type BrandColors = { readonly [K in BrandColorSlot]: string | null };

/**
 * The three typography slots.
 *
 * Carried through this app but never edited by it: the Go client deliberately
 * does not apply typography, so exposing a control would only let an operator
 * move a site away from its intended pairing. They matter here because an
 * omitted key on write clears the slot, so the values have to survive a
 * colours-only save.
 */
export const BRAND_TYPOGRAPHY_SLOTS = ['heading', 'body', 'numeric'] as const;

export type BrandTypographySlot = (typeof BRAND_TYPOGRAPHY_SLOTS)[number];

export type BrandTypography = {
  readonly [K in BrandTypographySlot]: string | null;
};

/**
 * The four logo slots.
 *
 * All four are modelled so a record round-trips faithfully, even though this
 * app only offers controls for the two the Go client currently consumes.
 */
export const BRAND_ASSET_SLOTS = [
  'headerLogo',
  'primaryLogo',
  'splashLogo',
  'favicon',
] as const;

export type BrandAssetSlot = (typeof BRAND_ASSET_SLOTS)[number];

/**
 * The asset slots this app provides upload and removal controls for.
 *
 * `primaryLogo` and `favicon` are accepted by the backend but have no consumer
 * in the Go client yet, so offering them would promise something players would
 * never see.
 */
export const EDITABLE_ASSET_SLOTS = ['headerLogo', 'splashLogo'] as const;

export type EditableAssetSlot = (typeof EDITABLE_ASSET_SLOTS)[number];

/**
 * How each editable slot is presented.
 *
 * Spelled out rather than derived from the slot name so each one can say where it
 * actually appears — an operator uploading a logo wants to know which screen it
 * lands on, not that the field is called `splashLogo`.
 */
export const EDITABLE_ASSET_SLOT_INFO: Readonly<
  Record<EditableAssetSlot, { readonly label: string; readonly description: string }>
> = {
  headerLogo: {
    label: 'Header Logo',
    description: 'Shown in the app header, beside the Toptracer mark.',
  },
  splashLogo: {
    label: 'Splash Logo',
    description: 'Shown on the bay setup screen, before a player starts hitting.',
  },
};

/**
 * A stored logo.
 *
 * `widthPx` and `heightPx` are the PNG's intrinsic pixels, derived by the
 * backend from the uploaded bytes. They describe an aspect ratio, not a CSS
 * size — nothing about an asset's identity or dimensions comes from a client.
 */
export interface BrandAsset {
  readonly url: string;
  readonly widthPx: number;
  readonly heightPx: number;
  readonly alt: string;
}

export type BrandAssets = {
  readonly [K in BrandAssetSlot]: BrandAsset | null;
};

/**
 * One branding record — either what players are served, or a pending draft.
 */
export interface BrandRecord {
  readonly siteId: string;
  readonly version: number;
  readonly colors: BrandColors;
  readonly typography: BrandTypography;
  readonly assets: BrandAssets;
  readonly updatedAt: string;
  /** Keycloak user id of whoever last wrote this record. */
  readonly updatedBy: string;
}

/**
 * Both records for a site.
 *
 * `draft === null` means nothing is pending. `published === null` with a draft
 * present is a site mid-first-launch — an ordinary state, not an error. Both
 * `null` means the site is unbranded.
 */
export interface BrandingState {
  readonly published: BrandRecord | null;
  readonly draft: BrandRecord | null;
}

/**
 * What the editor holds: the raw contents of the colour inputs.
 *
 * Deliberately `string` rather than `string | null`, because a half-typed
 * `#0B5` has to be storable without being a valid colour. Empty means unset.
 * Conversion to the wire's `string | null` happens in the request builder.
 */
export type BrandColorsDraft = { readonly [K in BrandColorSlot]: string };

/** An empty editor: every slot unset. */
export const EMPTY_COLORS_DRAFT: BrandColorsDraft = {
  accent: '',
  onAccent: '',
  header: '',
  onHeader: '',
  surface: '',
  onSurface: '',
  raised: '',
  onRaised: '',
};

/** Typography with every slot unset, for a site that has no record yet. */
export const EMPTY_TYPOGRAPHY: BrandTypography = {
  heading: null,
  body: null,
  numeric: null,
};

/**
 * Load a stored record's colours into the editor.
 *
 * `null` becomes an empty field: both mean "unset", and the editor works in
 * strings so an incomplete value is representable.
 */
export const toColorsDraft = (colors: BrandColors): BrandColorsDraft =>
  BRAND_COLOR_SLOTS.reduce(
    (acc, slot) => ({ ...acc, [slot]: colors[slot] ?? '' }),
    {} as { [K in BrandColorSlot]: string }
  );
